import React from "react";

import { Link } from "react-router-dom";
import moment from "moment";

import Callout from "ui/Callout";
import Grid, { GridColumn } from "ui/Grid";
import Loading from "ui/Loading";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";

import { formatMs, formatPercent } from "utils/format";
import { useRoutes } from "utils/routes";

import { useTopSlowQueriesForNewWorkbookQuery } from "./gql/TopSlowQueries.generated";

type TopQueryRow = {
  id: string;
  truncatedQuery: string;
  normalizedQuery: string;
  avgTime: number;
  callsPerMinute: number;
  pctOfTotal: number;
  sampleCount: number;
};

const LIMIT = 10;
const LOOKBACK_HOURS = 24;
// Bucket the sample count above this so a precise (and potentially large) number
// doesn't read as more meaningful than "plenty to pick from".
const SAMPLE_COUNT_CAP = 5;

const TopSlowQueries = ({ databaseId }: { databaseId: string }) => {
  const { databaseWorkbooksNew, databaseQueries } = useRoutes();

  const endTs = React.useMemo(() => moment().unix(), []);
  const startTs = React.useMemo(
    () => moment.unix(endTs).subtract(LOOKBACK_HOURS, "hours").unix(),
    [endTs],
  );

  const { data, loading, error } = useTopSlowQueriesForNewWorkbookQuery({
    variables: {
      databaseId,
      startTs,
      endTs,
      // "read" maps server-side to statement_types = '{SelectStmt}', matching
      // the workbook-supported set (see QueryRunService.SUPPORTED_STATEMENTS).
      statementTypes: ["read"],
      limit: LIMIT,
      sortBy: "pctOfTotal",
      sortDirection: "DESC",
    },
  });

  if (error) {
    return null;
  }

  const queries = data?.getQueryStats ?? [];
  // "normalize"/"all" redact sample parameters, so a high sample count may not
  // translate into usable parameter sets to seed a workbook from.
  const filterQuerySample =
    data?.getDatabaseDetails?.server?.collectorInfo?.filterQuerySample;
  const samplesFiltered =
    filterQuerySample === "normalize" || filterQuerySample === "all";

  if (!loading && queries.length === 0) {
    return (
      <Callout className="mt-4" thin>
        Don't have a query in mind? Pick one from the{" "}
        <Link to={databaseQueries(databaseId)}>Query Performance</Link> page to
        start a workbook from an existing query.
      </Callout>
    );
  }

  const columns: GridColumn<TopQueryRow, keyof TopQueryRow>[] = [
    {
      field: "normalizedQuery",
      header: "Query",
      width: "4fr",
      style: "query",
      disableSort: true,
      renderer: function QueryCell({ fieldData, rowData }) {
        return (
          <Link
            to={databaseWorkbooksNew(databaseId, rowData.id)}
            title={fieldData}
          >
            {rowData.truncatedQuery}
          </Link>
        );
      },
    },
    {
      field: "avgTime",
      header: "Avg Time",
      width: "minmax(90px,12%)",
      style: "number",
      defaultSortOrder: "DESC",
      renderer: function AvgTimeCell({ fieldData }) {
        return formatMs(fieldData);
      },
    },
    {
      field: "callsPerMinute",
      header: "Calls / min",
      width: "minmax(90px,12%)",
      style: "number",
      defaultSortOrder: "DESC",
      renderer: function CallsCell({ fieldData }) {
        return fieldData.toFixed(2);
      },
    },
    {
      field: "pctOfTotal",
      header: "% of All Runtime",
      width: "minmax(110px,14%)",
      style: "number",
      defaultSortOrder: "DESC",
      renderer: function PctCell({ fieldData }) {
        return formatPercent(fieldData / 100);
      },
    },
    {
      field: "sampleCount",
      header: "# Samples",
      width: "minmax(100px,12%)",
      style: "number",
      defaultSortOrder: "DESC",
      renderer: function SampleCountCell({ fieldData }) {
        return fieldData > SAMPLE_COUNT_CAP
          ? `${SAMPLE_COUNT_CAP}+`
          : fieldData;
      },
    },
  ];

  return (
    <Panel
      title="Or start from an existing query"
      secondaryTitle={`Top ${LIMIT} by % of total runtime, last ${LOOKBACK_HOURS}h`}
      className="mt-2"
    >
      {loading ? (
        <PanelSection>
          <Loading />
        </PanelSection>
      ) : (
        <>
          {samplesFiltered && (
            <PanelSection>
              <Callout thin>
                Query samples are{" "}
                <a
                  target="_blank"
                  href="https://pganalyze.com/docs/collector/pii-filtering"
                >
                  redacted
                </a>{" "}
                on this server, so the sample count may overstate how many
                parameter values you can use to seed a workbook.
              </Callout>
            </PanelSection>
          )}
          <Grid
            striped
            data={queries}
            columns={columns}
            defaultSortBy="pctOfTotal"
          />
        </>
      )}
    </Panel>
  );
};

export default TopSlowQueries;
